import { createContext, useContext, useMemo, type ReactNode } from "react";
import type { ConfigClient } from "./ConfigClient";
import { HttpConfigClient } from "./HttpConfigClient";
import { MockConfigClient } from "./MockConfigClient";
import { withConfigTracking } from "./trackedClient";
import { API_BASE, API_KEY } from "../analytics/config";

/** Gateway configs settings base. `API_BASE` is the gateway mount root (e.g.
 *  "/coding-gateway/internal" or "https://api.ai21.com/tokenwise"); the settings
 *  CRUD and the analytics API are sibling sub-paths under it, so we append
 *  `/settings/configs` the same way the analytics client appends `/analytics/v1`. */
function settingsBase(): string | undefined {
  if (!API_BASE) return undefined;

  return `${API_BASE.replace(/\/+$/, "")}/settings/configs`;
}

const ConfigClientContext = createContext<ConfigClient | null>(null);

/** Real settings client when the gateway is configured; otherwise the mock. */
export function createDefaultConfigClient(): ConfigClient {
  const base = settingsBase();
  const client = base
    ? new HttpConfigClient(base, API_KEY)
    : new MockConfigClient();
  return withConfigTracking(client);
}

export function ConfigProvider({
  children,
  client,
}: {
  children: ReactNode;
  client?: ConfigClient;
}) {
  const value = useMemo(() => client ?? createDefaultConfigClient(), [client]);
  return (
    <ConfigClientContext.Provider value={value}>
      {children}
    </ConfigClientContext.Provider>
  );
}

export function useConfigClient(): ConfigClient {
  const client = useContext(ConfigClientContext);
  if (!client) {
    throw new Error("useConfigClient must be used within a ConfigProvider");
  }

  return client;
}
